import { API, APIMethod, CompetitionAPI, DesignerAPI, Endpoints, Factory, LMSAPI, ProjectAPI, TeamAPI } from '@brg/api';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { ProjectsGridActions } from "../reducers/ProjectsGridSlice";
import { DesignerTab } from "../constants/designerTab";
import { EMPTY_CONTENT_TAB } from 'shared/pages/designer/components/NoProjectsContent';
import { Guard } from '@brg/toolbox';
import { ContextViewTab } from 'shared/constants/contextViewTab';
import urlApi from './urlApi';
import { getNonNullParam, mergeArraysBySubscribedId } from 'shared/components/Subscribe/support';
import { SUBSCRIBE_VISIBILITY_STATUS } from 'shared/components/Subscribe/const';
import { merge } from 'shared/helpers/utils/filters';

const PROJECTS_COUNT_FOR_OFFSET = 20;
const PROJECTS_COUNT_START_OFFSET = 0;
const PROJECTS_COUNT_LECTURE_OFFSET = 19;

type Tabs = {
  [key: string]: string[] | string | null;
}

const calcOffsetValue = (page: number, count: number) => {
  return page <= count ? page : count;
}

const replaceIdsParams = (array: any[]) => {
  if (!array || array?.length === 0) {
    return { projectIds: ['']}
  }
  return { projectIds: array }
}

const getFinalFilter = (tab: any, filter: any, replaceParamsList?: any, isNoTabs?: boolean) => {
  const {
    path,
    filter: tabFilter,
    override,
    withoutEmptyParams
  } = tab;

  if (override) {
    return {
      ...filter,
      ...tabFilter,
    }
  }
 
  const hasOrFilter = !!tabFilter?.orFilter && tabFilter.orFilter.length > 0 && !isNoTabs;
  const hasMainFilterOrFilter = !!filter?.orFilter && filter.orFilter.length > 0;
  
  if(hasOrFilter){
    const baseFilter = merge(filter, tabFilter);
    const mergedWithOrFilter = tabFilter.orFilter.reduce((acc: any, orItem: any) => {
      return merge(acc, orItem);
    }, filter);
    
    const processedFilter = {
      ...baseFilter,
      orFilter: [mergedWithOrFilter],
      ...([DesignerTab.drafts, DesignerTab.draftBooks].includes(path) ? { projectFilter: 9 } : {}),
      ...(replaceParamsList)
    };
    
    return processedFilter;
  }

  if(hasMainFilterOrFilter){
    const updatedOrFilter = filter.orFilter?.map((item: any) => ({
      ...item,
      projectStatus: filter?.projectStatus
    })) || [];
    
    const processedFilter = {
      ...filter,
      orFilter: updatedOrFilter,
      ...([DesignerTab.drafts, DesignerTab.draftBooks].includes(path) ? { projectFilter: 9 } : {}),
      ...(replaceParamsList)
    };
    
    return processedFilter;
  }

  const mergedFilter = {
    ...(merge(filter, tabFilter)),
    ...([DesignerTab.drafts, DesignerTab.draftBooks].includes(path) ? { projectFilter: 9 } : {}),
    ...(replaceParamsList)
  }

  if (withoutEmptyParams) {
    return getNonNullParam(mergedFilter)
  }


  return {
    ...mergedFilter,
    ...(mergedFilter?.moderationTypes?.includes(7) || mergedFilter?.moderationTypes?.includes(8) ? {} : { excludeModerationTypes: [7,8] }),
  };

}

const fetchFeed = async (props: any, options: any = {}) => {
  const urls = new URLSearchParams();
  Object.keys(props).forEach((key) => {
    if (props[key] instanceof Array) {
      for (const val of props[key]) {
        urls.append(key, val);
      }
    } else {
      urls.append(key, props[key]);
    }
  });
  return await API.fetch(
    Guard.void(options.url, ProjectAPI.url) + '/api/project/GetSubscriptionProjects' + '?' + urls.toString(),
    options,
  );
}

const fetchMainPageList = async (props: any, options: any = {}) => {
  return await API.request()
    .endpoint(Endpoints.geograffee)
    .url('/api/project/GetMainPageList')
    .post()
    .body(props)
    .send(options);
}

const getMapProjects = Factory.createGetMethodExtended(ProjectAPI, '/api/project/GetMainPageLite');

const methods = {
  projects: ProjectAPI.GetList,
  competition: CompetitionAPI.GetList,
  competitions_new: CompetitionAPI.GetList,
  books: ProjectAPI.GetList,
  organizations: TeamAPI.GetList,
  educationSubscribes: TeamAPI.GetList,
  modules: LMSAPI.GetModulesList,
  main: ProjectAPI.GetMainPage,
  channels: ProjectAPI.GetMainPageList,
  mainList: fetchMainPageList,
  jury: CompetitionAPI.GetList,
  feed: fetchFeed,
  designer: DesignerAPI.GetList,
  map: getMapProjects,
}

let abortController: AbortController | null = null;

export const fetchProjectsGrid = createAsyncThunk(
  'projects/fetchProjectsGrid',
  async (args: any, { getState, dispatch }: { getState: any, dispatch: any }) => {
    const {
      filter,
      isChannel,
      channelOwnerId,
      accountId,
      currentCategory,
      currentTab,
      contextName,
      teachersOnly,
      teamId,
      isProfile,
      isPeoplePage,
      isCompetitions: isCompetitionsProp,
      isLMSHSE,
      isMe,
      isSchool,
      isLectureMedia,
      designerId,
      isHseSpaceDesignerPage
    } = args;

   
  
    const { tabs } = getState().projectsGrid;

    if (abortController) {
      abortController.abort();
    }

    abortController = new AbortController();

    const { signal } = abortController;

    dispatch(ProjectsGridActions.setTabsLoading({ currentCategory, value: true }));

    const mainTab = tabs[currentCategory].find(({ path }: any) => path === currentTab);

    const isLmsCtx = mainTab?.filter?.contexts?.includes('hsecourses');

    const isDefaultEmptyTab = currentTab === EMPTY_CONTENT_TAB && !isLmsCtx;
    const isNoTabs = currentTab === "all" && Object.values(tabs).length === 1;

    const isCompetitions = isCompetitionsProp || mainTab?.entityType === 'competitions_new';

    const hasOrFilter = !!mainTab?.filter?.orFilter && mainTab.filter.orFilter.length > 0 && !isNoTabs;
    const hasMainFilterOrFilter = !!filter?.orFilter && filter.orFilter.length > 0;    

    const mainMethod: APIMethod<any, any> =  
    ['people_in_detail', 'people']?.includes(mainTab.entityType) ? methods.designer : 
    (isDefaultEmptyTab) ? ((hasOrFilter || hasMainFilterOrFilter) ? methods.mainList : methods.main)
    : isCompetitions ? methods['competition'] 
    //@ts-ignore
    : (hasOrFilter || hasMainFilterOrFilter) ? methods.mainList : (methods[mainTab?.entityType] || methods.main);

    const paramsForReplace = {
      ...(teachersOnly ? {
        globalTypes: ['Cooperator', 'Employee'],
      } : {}),
      ...(isHseSpaceDesignerPage ? {
        globalTypes: [...mainTab.filter.globalTypes, 'hsecreativestudent'],
        contexts: ['hsespace', 'hsedesign']
      } : {}),

    }

    const finalFilter = getFinalFilter(mainTab, filter, paramsForReplace, isNoTabs);

    const onlyBooksParam = isSchool || isLMSHSE || isPeoplePage || isProfile || isCompetitions ? {} : !finalFilter.hasOwnProperty('onlyBooks') ? { onlyBooks: false} : {};

    if (mainTab?.entityType === 'competitions') {
      await ProjectAPI.GetParticipateCompetitions()
        .then((d) => d.json())
        .then((d) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data: d,
            paging: {
              pages: 1,
              page: 1,
              count: d.totalProjectsCount,
            },
            currentTab,
            currentCategory,
            path: mainTab.path,
            entityType: mainTab.entityType,
            isDrafts: mainTab.isDrafts,
            isFinalPage: true,
            contextName,
          }))
        });
    } else if (mainTab?.entityType === 'modules') {

        await LMSAPI.GetModulesList({
            teamId: teamId,
            ...(mainTab?.path && mainTab.path !== 'all' && { lMSModuleDisciplines: mainTab.path}),
            limit: finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET,
        }, { signal })
            .then((d) => d.json())
            .then(({ data, paging }) => {
                dispatch(ProjectsGridActions.setSingleTabData({
                    data,
                    paging,
                    currentTab,
                    currentCategory,
                    entityType: mainTab.entityType,
                    isDrafts: mainTab.isDrafts,
                    // @ts-ignore
                    isFinalPage: paging?.pages === paging?.page,
                    contextName
                }))
            })
    } else if(mainTab?.entityType === 'organizations' && !isCompetitions && !isProfile && isLMSHSE && !isPeoplePage) {

      await mainMethod({
        ...finalFilter,
        ...(finalFilter?.sort ? { sort: finalFilter.sort } : {}),
        offset: PROJECTS_COUNT_START_OFFSET
      }, { signal })
        .then((d) => d.json())
        .then(({ data, paging }) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data,
            paging,
            currentTab,
            currentCategory,
            entityType: mainTab.entityType,
            isDrafts: mainTab.isDrafts,
            isFinalPage: paging.pages === paging.page,
            contextName
          }))
        })
    } else if (mainTab?.entityType === DesignerTab.subscriptionsProjects) {
      await methods.feed({
        accountId: accountId,
        limit: finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET,
        ...(!!filter?.sort ? { sort: filter.sort} : {}),
        ...(!!filter?.tagIds ? { tagIds: filter.tagIds} : {}),
        ...(!!filter.hasProductCards ? { hasProductCards: filter.hasProductCards} : {})
      }, { signal })
      .then((d) => d.json())
      .then(({ data, paging }) => {
        dispatch(ProjectsGridActions.setSingleTabData({
          data,
          paging,
          currentTab,
          currentCategory,
          entityType: mainTab.entityType,
          isDrafts: false,
          isFinalPage: paging.pages === paging.page,
          contextName
        }))
      })
    } else if(mainTab?.entityType === DesignerTab.subscriptions) {
      const visibilityOpt = `${!isMe ? `&visibility=${SUBSCRIBE_VISIBILITY_STATUS.visible}` : ''}`;
      const offset = finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
      const subscriberType = '0'
      const targetType = 'userProjects'
      const params = `subscriberId=${accountId}&subscriberType=${subscriberType}&targetType=${targetType}&limit=${offset}${visibilityOpt} `
      const url = `${Endpoints.subscriptions}/api/Subscription/GetSubscriptions?${params}`

      await API.fetch(url, { signal })
      .then((r) => r.json())  
      .then(async(subscriptions) => {
        const { data } = subscriptions;
        const subsIds = data?.map((i: any) => i?.targetId);

        if (!subsIds?.length) return;

        await DesignerAPI.GetList({
          designerIds: subsIds,
          offset: finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
        }).then((r) => r.json())
        .then((r) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            // @ts-ignore
            data: mergeArraysBySubscribedId(r?.data, data),
            paging: r?.paging,
            currentTab,
            entityType: 'subscriptions',
            currentCategory,
            isFinalPage: false,
            contextName
          }))
        })
      })

    } else if(mainTab?.entityType === DesignerTab.subscribers) {
      const offset = finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
      const targetType = 'userProjects'
      const params = `targetId=${accountId}&targetType=${targetType}&limit=${offset}`
      const url = `${Endpoints.subscriptions}/api/Subscription/GetSubscribers?${params}`

      await API.fetch(url, { signal })
      .then((r) => r.json())
      .then(async(subscriptions) => {
        const { data, paging } = subscriptions;
        const subsIds = data?.map((i: any) => i?.subscriberId);

        if (!subsIds?.length) return;

        await DesignerAPI.GetList({
          designerIds: subsIds,
          offset: finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
        }).then((r) => r.json())
        .then((r) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            // @ts-ignore
            data: mergeArraysBySubscribedId(r?.data, data),
            paging: r?.paging,
            currentTab,
            entityType: 'subscribers',
            currentCategory,
            isFinalPage: paging?.pages === paging.page,
            contextName
          }))
        })
      })
    }else if (currentTab === 'discount') {
      await API.request()
      .endpoint(Endpoints.geograffee)
      .url('/api/Designer/GetDesignerDiscountCards')
      .addQuery('designerId', designerId)
      .get()
      .send({ signal })
      .then((r) => r.json())
      .then((cards) => {
        dispatch(ProjectsGridActions.setSingleTabData({
          data: cards,
          paging: {count : 20, page: 1, pages: 0},
          currentTab,
          currentCategory,
          entityType: mainTab.entityType,
          isDrafts: mainTab.isDrafts,
          isFinalPage: false,
          contextName
        }))
      })
    } else if (mainTab?.entityType === DesignerTab.competitionCerts) {
      await API.request()
        .endpoint(Endpoints.geograffee)
        .url('/api/Designer/GetCompetitionCerts')
        .addQuery('designerId', designerId)
        .get()
        .send({ signal })
        .then((r) => r.json())
        .then((cards) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data: cards,
            paging: {count : 20, page: 1, pages: 0},
            currentTab,
            currentCategory,
            entityType: mainTab.entityType,
            isDrafts: mainTab.isDrafts,
            isFinalPage: false,
            contextName
          }))
        })
    } else if (
      isPeoplePage && [
        ContextViewTab.teachers,
        ContextViewTab.students,
        ContextViewTab.alumnus
      ]?.includes(currentTab)
    ) {
      const {
        limit,
        globalTypes,
        isLite,
        sort,
        isContextIgnored,
        appNameIgnore
      } = mainTab?.filter;
        await API.request()
        .endpoint(Endpoints.geograffee)
        .url('/api/Designer/GetList')
        .addQuery('limit', limit)
        .addQuery('globalTypes', isSchool && currentTab === ContextViewTab.students? ['Hseyerevanstudent'] : globalTypes)
        .addQuery('sort', sort)
        .addQuery('isContextIgnored', isContextIgnored)
        .addQuery('appNameIgnore', appNameIgnore)
        .addQuery('isLite', isLite)
        .get()
        .send({ signal })
        .then((r) => r.json())
        .then((r) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data: r?.data,
            paging: r?.paging,
            currentTab,
            currentCategory,
            entityType: mainTab.entityType,
            isDrafts: mainTab.isDrafts,
            isFinalPage: false,
            contextName
          }))
        })
    } else {
      await mainMethod(isChannel ? {
          projectFilter: finalFilter?.projectFilter,
          projectStatus: finalFilter?.projectStatus,
          ...replaceIdsParams(finalFilter?.projectIds),
          ...(channelOwnerId && {authorIds: [channelOwnerId]}),
          limit: finalFilter?.limit,
          offset: PROJECTS_COUNT_START_OFFSET,
          isContextIgnored: true,
          isLite: false,
          mainPage: false
      } : {
        ...finalFilter,
        ...onlyBooksParam,
        limit: finalFilter?.limit,
        offset: PROJECTS_COUNT_START_OFFSET,
        isLite: false
      }, { signal })
        .then((d) => d.json())
        .then(({ data, paging }) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data,
            paging,
            currentTab,
            currentCategory,
            entityType: mainTab.entityType,
            isDrafts: mainTab.isDrafts,
            isFinalPage: paging.pages === paging.page,
            contextName
          }))
        })
    }
    
    const restTabs = tabs[currentCategory].filter(({ path, isSwitcher }: any) => path !== currentTab && !isSwitcher);

    const json = Promise.allSettled(

      restTabs.map((tab: Tabs) => {
        //todo Убрать дублирование. Убрать ситуационные пропсы для разных страниц, оставить только конфиг и прокидывать нужное сверху.
        const isCompetitions = isCompetitionsProp || tab?.entityType === 'competitions_new';
        const isNoTabs = currentTab === "all" && Object.values(tabs).length === 1;
        //@ts-ignore
        const hasOrFilter = !!tab?.filter?.orFilter && tab.filter.orFilter.length > 0 && !isNoTabs;
        const hasMainFilterOrFilter = !!filter?.orFilter && filter.orFilter.length > 0;

        const finalFilter = getFinalFilter(tab, filter, paramsForReplace, isNoTabs);
        
        const onlyBooksParam = isSchool || isLMSHSE || isPeoplePage || isProfile || isCompetitions ? {} : !finalFilter.hasOwnProperty('onlyBooks') ? { onlyBooks: false} : {};

        //@ts-ignore
        const method: APIMethod<any, any> = ['people_in_detail', 'people']?.includes(tab.entityType)? methods['designer'] : isCompetitions ? methods['competition'] : (hasOrFilter || hasMainFilterOrFilter) ? methods.mainList : (methods[tab?.entityType] || methods.main);

        if (tab?.entityType === DesignerTab.subscriptionsProjects ) {
          return (
            methods.feed({
              ...finalFilter,
              offset: PROJECTS_COUNT_START_OFFSET,
              accountId: accountId,
              limit: finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET,
              ...(!!filter?.sort ? { sort: filter.sort} : {}),
              ...(!!filter?.tagIds ? { tagIds: filter.tagIds} : {}),
              ...(!!filter.hasProductCards ? { hasProductCards: filter.hasProductCards} : {})
            })
              .then((d) => d.json())
              .then((d) => {
                return ({
                  data: d,
                  path: tab.path,
                  entityType: tab.entityType,
                  isDrafts: tab.isDrafts,
                })
              })
          )
        }
        if(tab?.entityType === DesignerTab.subscriptions) {
          const visibilityOpt = `${!isMe ? `&visibility=${SUBSCRIBE_VISIBILITY_STATUS.visible}` : ''}`;
          const offset = finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
          const subscriberType = '0'
          const targetType = 'userProjects';
          const params = `subscriberId=${accountId}&subscriberType=${subscriberType}&targetType=${targetType}&limit=${offset}${visibilityOpt}&page=1`
          const url = `${Endpoints.subscriptions}/api/Subscription/GetSubscriptions?${params}`

          return API.fetch(url, { signal })
          .then((r) => r.json())
          .then((subscriptions) => {
            const { data, paging } = subscriptions;
            const subsIds = data?.map((i: any) => i?.targetId);

            if (!subsIds?.length) return;

            return DesignerAPI.GetList({
              designerIds: subsIds,
              offset: offset
            }).then((r) => r.json())
            .then((r) => {
              return ({
                data: {
                  paging, 
                  // @ts-ignore
                  data: mergeArraysBySubscribedId(r?.data, data) 
                },
                path: tab.path,
                entityType: tab.entityType,
                isFinalPage: paging?.pages === paging.page,
              })
            })
          })
        }
        if(tab?.entityType === DesignerTab.subscribers) {

          const offset = finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
          const targetType = 'userProjects'
          const params = `targetId=${accountId}&targetType=${targetType}&limit=${offset}&page=1`
          const url = `${Endpoints.subscriptions}/api/Subscription/GetSubscribers?${params}`
    
          return API.fetch(url, { signal })
          .then((r) => r.json())
          .then(async(subscriptions) => {
            const { data, paging } = subscriptions;
            const subsIds = data?.map((i: any) => i?.subscriberId);

            if (!subsIds?.length) return;

            return DesignerAPI.GetList({
              designerIds: subsIds,
              offset: finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
            }).then((r) => r.json())
            .then((r) => {
              return ({
                // @ts-ignore
                data: { paging, data: mergeArraysBySubscribedId(r?.data, data) },
                path: tab.path,
                entityType: tab.entityType,
                isFinalPage: paging?.pages === paging.page,
              })
            })
          })
        }
        if (tab?.entityType === 'discount') {
          return API.request()
          .endpoint(Endpoints.geograffee)
          .url('/api/Designer/GetDesignerDiscountCards')
          .addQuery('designerId', designerId)
          .get()
          .send({ signal })
          .then((r) => r.json())
          .then((cards) => {
          return ({
            data: {
              data: cards,
              paging: {count : 20, page: 1, pages: 1},
            },
            path: tab.path,
            entityType: tab.entityType,
            isDrafts: false,
            isFinalPage: true,
          })
          }
          )
        }
        if (tab?.entityType === DesignerTab.competitionCerts) {
          return API.request()
          .endpoint(Endpoints.geograffee)
            .url('/api/Designer/GetCompetitionCerts')
            .addQuery('designerId', designerId)
            .get()
            .send({ signal })
            .then((r) => r.json())
            .then((cards) => {
              return ({
                data: {
                  data: cards,
                  paging: {count : 20, page: 1, pages: 1},
                },
                path: tab.path,
                entityType: tab.entityType,
                isDrafts: false,
                isFinalPage: true,
              })
            });
        }
        if (tab?.entityType === 'competitions') {
          return ProjectAPI.GetParticipateCompetitions()
            .then((d) => d.json())
            .then((d) => {
              return ({
                data: {
                  data: d,
                  paging: {
                    pages: 1,
                    page: 1,
                    count: d.totalProjectsCount,
                  },
                },
                paging: {
                  pages: 1,
                  page: 1,
                  count: d.totalProjectsCount,
                },
                path: tab.path,
                entityType: tab.entityType,
                isDrafts: tab.isDrafts,
                isFinalPage: true,
              })
            });
        }
        if(tab?.entityType === 'modules'){
            return LMSAPI.GetModulesList({
            teamId: teamId,
            ...(tab?.path && tab.path !== 'all' && { lMSModuleDisciplines: tab.path }),
            limit: finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
        })
            .then((d) => d.json())
            .then((d) => {
              return ({
                data: d,
                path: tab.path,
                entityType: tab.entityType,
                isDrafts: tab.isDrafts,
              })
            })
        }
        if(mainTab?.entityType === 'organizations' && !isProfile && !isCompetitions && isLMSHSE && !isPeoplePage){
          
          return (
            method({
              ...finalFilter,
              ...(finalFilter?.sort ? { sort: finalFilter.sort } : {}),
              offset: PROJECTS_COUNT_START_OFFSET,
                //todo выяснить, нужно ли это сейчас
              // lMSCourseType: (mainTab?.entityType === 'organizations' && !isProfile && !isCompetitions) && lMSCourseType,
            })
              .then((d) => d.json())
              .then((d) => {
                return ({
                  data: d,
                  path: tab.path,
                  entityType: tab.entityType,
                  isDrafts: tab.isDrafts,
                })
              })
          )
        }
        if (
          isPeoplePage
          && [
            ContextViewTab.teachers,
            ContextViewTab.students,
            ContextViewTab.alumnus
          ]?.includes(tab.name as any)
        ) {
          const {
            limit,
            globalTypes,
            sort,
            isLite,
            isContextIgnored,
            appNameIgnore
          } = tab?.filter as any;
            return DesignerAPI.GetList({
              limit,
              globalTypes,
              sort,
              isLite,
              isContextIgnored,
              appNameIgnore
            }).then((r) => r.json())
            .then((r) => {
              return ({
                data: r?.data,
                path: tab.path,
                entityType: tab.entityType,
                isDrafts: tab.isDrafts,
              })
            })
        }

        return (
          method(isChannel ? {
              projectFilter: finalFilter?.projectFilter,
              projectStatus: finalFilter?.projectStatus,
              ...replaceIdsParams(finalFilter?.projectIds),
              ...(channelOwnerId && {authorIds: [channelOwnerId]}),
              limit: finalFilter?.limit,
              offset: PROJECTS_COUNT_START_OFFSET,
              isContextIgnored: true,
              isLite: false,
              mainPage: false
          } : {
            ...finalFilter,
            ...onlyBooksParam,
              ...(isChannel && {
                isContextIgnored: true, 
                mainPage: false, 
                ...replaceIdsParams(finalFilter?.projectIds),
                projectStatus: finalFilter?.projectStatus,
              }),
              ...(isProfile ? { isLite: false } : {}),
              offset: PROJECTS_COUNT_START_OFFSET,
          })
            .then((d) => d.json())
            .then((d) => {
              return ({
                data: d,
                path: tab.path,
                entityType: tab.entityType,
                isDrafts: tab.isDrafts,
              })
            })
        )
      }))
      .then((res: any) => {
          return {
            contextName,
            currentCategory,
            data: res.reduce((acc: any, tab: any) => {
              if (tab?.status !== 'fulfilled') return acc;
              const { value } = tab;
              if (!value) return acc;
              const { data: { data, paging }, path, entityType, isDrafts } = value;
              return {
                ...acc,
                [path]: {
                  data,
                  paging,
                  entityType,
                  isDrafts,
                  isFinalPage: paging.pages === paging.page
                },
              }
            }, {})
          };
        }
      )
      //временная затычка, удалить, когда у этой вкладки появятся свой запрос
    return json;
  }
);

export const fetchProjectsGridMore = createAsyncThunk(
  'projects/fetchProjectsGridMore',
  async (args: any, { getState }: { getState: any }) => {
    const { currentTab, currentCategory, isChannel, filter, accountId, teachersOnly, isLMSHSE, isProfile, isCompetitions, isMe, isMedia, isPeoplePage, isSchool, contextName, isHseSpaceDesignerPage } = args;
    const { tabs, data } = getState().projectsGrid;

    const tab = tabs[currentCategory].find(({ path }: any) => path === currentTab);
    const { paging }: any = data[currentCategory][currentTab];

    const paramsForReplace = {
      ...(teachersOnly ? {
        globalTypes: ['Cooperator', 'Employee'],
      } : {}),
      ...(isSchool && currentTab === ContextViewTab.students ? {
        globalTypes: ['Hseyerevanstudent'],
      } : {}),
      ...(isHseSpaceDesignerPage ? {
        globalTypes: [...tab.filter.globalTypes, 'hsecreativestudent'],
        contexts: ['hsespace', 'hsedesign']
      } : {}),
    }

    const isNoTabs = currentTab === "all" && Object.values(tabs).length === 1;
    const hasOrFilter = !!tab?.filter?.orFilter && tab.filter.orFilter.length > 0 && !isNoTabs;
    const hasMainFilterOrFilter = !!filter?.orFilter && filter.orFilter.length > 0;
    const finalFilter = getFinalFilter(tab, filter, paramsForReplace, isNoTabs);

    const onlyBooksParam = isSchool || isLMSHSE || isPeoplePage || isProfile || isCompetitions ? {} : !finalFilter.hasOwnProperty('onlyBooks') ? { onlyBooks: false} : {};

    const method: APIMethod<any, any> = 
      tab?.entityType === DesignerTab.subscriptionsProjects ? methods.feed : 
      isPeoplePage || ['people_in_detail', 'people']?.includes(tab.entityType) ?
      methods.designer :
      //@ts-ignore
      (hasOrFilter || hasMainFilterOrFilter) ? methods.mainList : (methods[tab?.entityType] || methods.main);

      if (tab?.entityType === DesignerTab.subscriptions) {
        const json = await urlApi()?.fetchSubscriptions({ 
            subscriberId: accountId, 
            page: paging.page + 1,
            offset: paging.offset + filter.limit,
            limit: PROJECTS_COUNT_FOR_OFFSET,
            tab,
            isMe,
          })
        
        return {
          currentTab,
          currentCategory,
          entityType: tab.entityType,
          paging: json.paging,
          data: json.data,
          //@ts-ignore
          isFinalPage: json.paging.pages === json?.paging?.page
        };
      }

      if (tab?.entityType === DesignerTab.subscribers) {
        const json = await urlApi()?.fetchSubscribers({ 
          subscriberId: accountId, 
          page: paging.page + 1,
          offset: paging.offset + filter.limit,
          limit: PROJECTS_COUNT_FOR_OFFSET,
          tab,
        })

        return {
          currentTab,
          currentCategory,
          entityType: tab.entityType,
          paging: json.paging,
          data: json.data,
          //@ts-ignore
          isFinalPage: json.paging.pages === json?.paging?.page
        };
      }

    const json = await API.Json(
      method({
        ...finalFilter,
        ...onlyBooksParam,
        isLite: false,
        offset: paging.offset + filter.limit,
        page: paging.page + 1,
        ...(isChannel ? {
          ...replaceIdsParams(finalFilter?.projectIds), 
          projectStatus: finalFilter?.projectStatus,
        } : {}),
        ...(tab?.entityType === DesignerTab.subscriptionsProjects ? { accountId } : {}),
        ...(tab?.withoutEmptyParams ? {} : filter?.sort ? { sort: filter?.sort} : {})
      }),
    )

    return {
      currentTab,
      currentCategory,
      entityType: tab.entityType,
      paging: json.paging,
      data: json.data,
      //@ts-ignore
      isFinalPage: json.paging.pages <= json?.paging?.page
    };
  }
);

export const fetchInfiniteProjectsWithUpdate = createAsyncThunk(
  'projects/fetchInfiniteProjectsWithUpdate',
  async (args: any, { getState }: { getState: any }) => {
    const { page, lang } = args;
    const { filter, paging } = getState().projects;

    const json = await API.Json(
      ProjectAPI.GetMainPage({
        ...filter,
        offset: calcOffsetValue(
          (Number(page) * PROJECTS_COUNT_FOR_OFFSET
          ),
          paging?.count
        ) - PROJECTS_COUNT_FOR_OFFSET
      }),
    )
    return lang ? (API.lang({
      paging: json.paging,
      data: [...json.data],
      //@ts-ignore
      isFinalPage: json.paging.pages <= json?.paging?.page
    }, lang) as any) : {
      paging: json.paging,
      data: [...json.data],
      //@ts-ignore
      isFinalPage: json.paging.pages <= json?.paging?.page
    };
  }
);
import { API, APIMethod, CompetitionAPI, DesignerAPI, Endpoints, Factory, LMSAPI, ProjectAPI, TeamAPI } from '@brg/api';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { ProjectsGridActions } from "../reducers/ProjectsGridSlice";
import { DesignerTab } from "../constants/designerTab";
import { EMPTY_CONTENT_TAB } from 'shared/pages/designer/components/NoProjectsContent';
import { Guard } from '@brg/toolbox';
import { ContextViewTab } from 'shared/constants/contextViewTab';
import urlApi from './urlApi';
import { getNonNullParam, mergeArraysBySubscribedId } from 'shared/components/Subscribe/support';
import { SUBSCRIBE_VISIBILITY_STATUS } from 'shared/components/Subscribe/const';
import { merge } from 'shared/helpers/utils/filters';

const PROJECTS_COUNT_FOR_OFFSET = 20;
const PROJECTS_COUNT_START_OFFSET = 0;
const PROJECTS_COUNT_LECTURE_OFFSET = 19;

type Tabs = {
  [key: string]: string[] | string | null;
}

const calcOffsetValue = (page: number, count: number) => {
  return page <= count ? page : count;
}

const replaceIdsParams = (array: any[]) => {
  if (!array || array?.length === 0) {
    return { projectIds: ['']}
  }
  return { projectIds: array }
}

const getFinalFilter = (tab: any, filter: any, replaceParamsList?: any, isNoTabs?: boolean) => {
  const {
    path,
    filter: tabFilter,
    override,
    withoutEmptyParams
  } = tab;

  if (override) {
    return {
      ...filter,
      ...tabFilter,
    }
  }
 
  const hasOrFilter = !!tabFilter?.orFilter && tabFilter.orFilter.length > 0 && !isNoTabs;
  const hasMainFilterOrFilter = !!filter?.orFilter && filter.orFilter.length > 0;
  
  if(hasOrFilter){
    const baseFilter = merge(filter, tabFilter);
    const mergedWithOrFilter = tabFilter.orFilter.reduce((acc: any, orItem: any) => {
      return merge(acc, orItem);
    }, filter);
    
    const processedFilter = {
      ...baseFilter,
      orFilter: [mergedWithOrFilter],
      ...([DesignerTab.drafts, DesignerTab.draftBooks].includes(path) ? { projectFilter: 9 } : {}),
      ...(replaceParamsList)
    };
    
    return processedFilter;
  }

  if(hasMainFilterOrFilter){
    const updatedOrFilter = filter.orFilter?.map((item: any) => ({
      ...item,
      projectStatus: filter?.projectStatus
    })) || [];
    
    const processedFilter = {
      ...filter,
      orFilter: updatedOrFilter,
      ...([DesignerTab.drafts, DesignerTab.draftBooks].includes(path) ? { projectFilter: 9 } : {}),
      ...(replaceParamsList)
    };
    
    return processedFilter;
  }

  const mergedFilter = {
    ...(merge(filter, tabFilter)),
    ...([DesignerTab.drafts, DesignerTab.draftBooks].includes(path) ? { projectFilter: 9 } : {}),
    ...(replaceParamsList)
  }

  if (withoutEmptyParams) {
    return getNonNullParam(mergedFilter)
  }


  return {
    ...mergedFilter,
    ...(mergedFilter?.moderationTypes?.includes(7) || mergedFilter?.moderationTypes?.includes(8) ? {} : { excludeModerationTypes: [7,8] }),
  };

}

const fetchFeed = async (props: any, options: any = {}) => {
  const urls = new URLSearchParams();
  Object.keys(props).forEach((key) => {
    if (props[key] instanceof Array) {
      for (const val of props[key]) {
        urls.append(key, val);
      }
    } else {
      urls.append(key, props[key]);
    }
  });
  return await API.fetch(
    Guard.void(options.url, ProjectAPI.url) + '/api/project/GetSubscriptionProjects' + '?' + urls.toString(),
    options,
  );
}

const fetchMainPageList = async (props: any, options: any = {}) => {
  return await API.request()
    .endpoint(Endpoints.geograffee)
    .url('/api/project/GetMainPageList')
    .post()
    .body(props)
    .send(options);
}

const getMapProjects = Factory.createGetMethodExtended(ProjectAPI, '/api/project/GetMainPageLite');

const methods = {
  projects: ProjectAPI.GetList,
  competition: CompetitionAPI.GetList,
  competitions_new: CompetitionAPI.GetList,
  books: ProjectAPI.GetList,
  organizations: TeamAPI.GetList,
  educationSubscribes: TeamAPI.GetList,
  modules: LMSAPI.GetModulesList,
  main: ProjectAPI.GetMainPage,
  channels: ProjectAPI.GetMainPageList,
  mainList: fetchMainPageList,
  jury: CompetitionAPI.GetList,
  feed: fetchFeed,
  designer: DesignerAPI.GetList,
  map: getMapProjects,
}

let abortController: AbortController | null = null;

export const fetchProjectsGrid = createAsyncThunk(
  'projects/fetchProjectsGrid',
  async (args: any, { getState, dispatch }: { getState: any, dispatch: any }) => {
    const {
      filter,
      isChannel,
      channelOwnerId,
      accountId,
      currentCategory,
      currentTab,
      contextName,
      teachersOnly,
      teamId,
      isProfile,
      isPeoplePage,
      isCompetitions: isCompetitionsProp,
      isLMSHSE,
      isMe,
      isSchool,
      isLectureMedia,
      designerId,
      isHseSpaceDesignerPage
    } = args;

   
  
    const { tabs } = getState().projectsGrid;

    if (abortController) {
      abortController.abort();
    }

    abortController = new AbortController();

    const { signal } = abortController;

    dispatch(ProjectsGridActions.setTabsLoading({ currentCategory, value: true }));

    const mainTab = tabs[currentCategory].find(({ path }: any) => path === currentTab);

    const isLmsCtx = mainTab?.filter?.contexts?.includes('hsecourses');

    const isDefaultEmptyTab = currentTab === EMPTY_CONTENT_TAB && !isLmsCtx;
    const isNoTabs = currentTab === "all" && Object.values(tabs).length === 1;

    const isCompetitions = isCompetitionsProp || mainTab?.entityType === 'competitions_new';

    const hasOrFilter = !!mainTab?.filter?.orFilter && mainTab.filter.orFilter.length > 0 && !isNoTabs;
    const hasMainFilterOrFilter = !!filter?.orFilter && filter.orFilter.length > 0;    

    const mainMethod: APIMethod<any, any> =  
    ['people_in_detail', 'people']?.includes(mainTab.entityType) ? methods.designer : 
    (isDefaultEmptyTab) ? ((hasOrFilter || hasMainFilterOrFilter) ? methods.mainList : methods.main)
    : isCompetitions ? methods['competition'] 
    //@ts-ignore
    : (hasOrFilter || hasMainFilterOrFilter) ? methods.mainList : (methods[mainTab?.entityType] || methods.main);

    const paramsForReplace = {
      ...(teachersOnly ? {
        globalTypes: ['Cooperator', 'Employee'],
      } : {}),
      ...(isHseSpaceDesignerPage ? {
        globalTypes: [...mainTab.filter.globalTypes, 'hsecreativestudent'],
        contexts: ['hsespace', 'hsedesign']
      } : {}),

    }

    const finalFilter = getFinalFilter(mainTab, filter, paramsForReplace, isNoTabs);

    const onlyBooksParam = isSchool || isLMSHSE || isPeoplePage || isProfile || isCompetitions ? {} : !finalFilter.hasOwnProperty('onlyBooks') ? { onlyBooks: false} : {};

    if (mainTab?.entityType === 'competitions') {
      await ProjectAPI.GetParticipateCompetitions()
        .then((d) => d.json())
        .then((d) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data: d,
            paging: {
              pages: 1,
              page: 1,
              count: d.totalProjectsCount,
            },
            currentTab,
            currentCategory,
            path: mainTab.path,
            entityType: mainTab.entityType,
            isDrafts: mainTab.isDrafts,
            isFinalPage: true,
            contextName,
          }))
        });
    } else if (mainTab?.entityType === 'modules') {

        await LMSAPI.GetModulesList({
            teamId: teamId,
            ...(mainTab?.path && mainTab.path !== 'all' && { lMSModuleDisciplines: mainTab.path}),
            limit: finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET,
        }, { signal })
            .then((d) => d.json())
            .then(({ data, paging }) => {
                dispatch(ProjectsGridActions.setSingleTabData({
                    data,
                    paging,
                    currentTab,
                    currentCategory,
                    entityType: mainTab.entityType,
                    isDrafts: mainTab.isDrafts,
                    // @ts-ignore
                    isFinalPage: paging?.pages === paging?.page,
                    contextName
                }))
            })
    } else if(mainTab?.entityType === 'organizations' && !isCompetitions && !isProfile && isLMSHSE && !isPeoplePage) {

      await mainMethod({
        ...finalFilter,
        ...(finalFilter?.sort ? { sort: finalFilter.sort } : {}),
        offset: PROJECTS_COUNT_START_OFFSET
      }, { signal })
        .then((d) => d.json())
        .then(({ data, paging }) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data,
            paging,
            currentTab,
            currentCategory,
            entityType: mainTab.entityType,
            isDrafts: mainTab.isDrafts,
            isFinalPage: paging.pages === paging.page,
            contextName
          }))
        })
    } else if (mainTab?.entityType === DesignerTab.subscriptionsProjects) {
      await methods.feed({
        accountId: accountId,
        limit: finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET,
        ...(!!filter?.sort ? { sort: filter.sort} : {}),
        ...(!!filter?.tagIds ? { tagIds: filter.tagIds} : {}),
        ...(!!filter.hasProductCards ? { hasProductCards: filter.hasProductCards} : {})
      }, { signal })
      .then((d) => d.json())
      .then(({ data, paging }) => {
        dispatch(ProjectsGridActions.setSingleTabData({
          data,
          paging,
          currentTab,
          currentCategory,
          entityType: mainTab.entityType,
          isDrafts: false,
          isFinalPage: paging.pages === paging.page,
          contextName
        }))
      })
    } else if(mainTab?.entityType === DesignerTab.subscriptions) {
      const visibilityOpt = `${!isMe ? `&visibility=${SUBSCRIBE_VISIBILITY_STATUS.visible}` : ''}`;
      const offset = finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
      const subscriberType = '0'
      const targetType = 'userProjects'
      const params = `subscriberId=${accountId}&subscriberType=${subscriberType}&targetType=${targetType}&limit=${offset}${visibilityOpt} `
      const url = `${Endpoints.subscriptions}/api/Subscription/GetSubscriptions?${params}`

      await API.fetch(url, { signal })
      .then((r) => r.json())  
      .then(async(subscriptions) => {
        const { data } = subscriptions;
        const subsIds = data?.map((i: any) => i?.targetId);

        if (!subsIds?.length) return;

        await DesignerAPI.GetList({
          designerIds: subsIds,
          offset: finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
        }).then((r) => r.json())
        .then((r) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            // @ts-ignore
            data: mergeArraysBySubscribedId(r?.data, data),
            paging: r?.paging,
            currentTab,
            entityType: 'subscriptions',
            currentCategory,
            isFinalPage: false,
            contextName
          }))
        })
      })

    } else if(mainTab?.entityType === DesignerTab.subscribers) {
      const offset = finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
      const targetType = 'userProjects'
      const params = `targetId=${accountId}&targetType=${targetType}&limit=${offset}`
      const url = `${Endpoints.subscriptions}/api/Subscription/GetSubscribers?${params}`

      await API.fetch(url, { signal })
      .then((r) => r.json())
      .then(async(subscriptions) => {
        const { data, paging } = subscriptions;
        const subsIds = data?.map((i: any) => i?.subscriberId);

        if (!subsIds?.length) return;

        await DesignerAPI.GetList({
          designerIds: subsIds,
          offset: finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
        }).then((r) => r.json())
        .then((r) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            // @ts-ignore
            data: mergeArraysBySubscribedId(r?.data, data),
            paging: r?.paging,
            currentTab,
            entityType: 'subscribers',
            currentCategory,
            isFinalPage: paging?.pages === paging.page,
            contextName
          }))
        })
      })
    }else if (currentTab === 'discount') {
      await API.request()
      .endpoint(Endpoints.geograffee)
      .url('/api/Designer/GetDesignerDiscountCards')
      .addQuery('designerId', designerId)
      .get()
      .send({ signal })
      .then((r) => r.json())
      .then((cards) => {
        dispatch(ProjectsGridActions.setSingleTabData({
          data: cards,
          paging: {count : 20, page: 1, pages: 0},
          currentTab,
          currentCategory,
          entityType: mainTab.entityType,
          isDrafts: mainTab.isDrafts,
          isFinalPage: false,
          contextName
        }))
      })
    } else if (mainTab?.entityType === DesignerTab.competitionCerts) {
      await API.request()
        .endpoint(Endpoints.geograffee)
        .url('/api/Designer/GetCompetitionCerts')
        .addQuery('designerId', designerId)
        .get()
        .send({ signal })
        .then((r) => r.json())
        .then((cards) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data: cards,
            paging: {count : 20, page: 1, pages: 0},
            currentTab,
            currentCategory,
            entityType: mainTab.entityType,
            isDrafts: mainTab.isDrafts,
            isFinalPage: false,
            contextName
          }))
        })
    } else if (
      isPeoplePage && [
        ContextViewTab.teachers,
        ContextViewTab.students,
        ContextViewTab.alumnus
      ]?.includes(currentTab)
    ) {
      const {
        limit,
        globalTypes,
        isLite,
        sort,
        isContextIgnored,
        appNameIgnore
      } = mainTab?.filter;
        await API.request()
        .endpoint(Endpoints.geograffee)
        .url('/api/Designer/GetList')
        .addQuery('limit', limit)
        .addQuery('globalTypes', isSchool && currentTab === ContextViewTab.students? ['Hseyerevanstudent'] : globalTypes)
        .addQuery('sort', sort)
        .addQuery('isContextIgnored', isContextIgnored)
        .addQuery('appNameIgnore', appNameIgnore)
        .addQuery('isLite', isLite)
        .get()
        .send({ signal })
        .then((r) => r.json())
        .then((r) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data: r?.data,
            paging: r?.paging,
            currentTab,
            currentCategory,
            entityType: mainTab.entityType,
            isDrafts: mainTab.isDrafts,
            isFinalPage: false,
            contextName
          }))
        })
    } else {
      await mainMethod(isChannel ? {
          projectFilter: finalFilter?.projectFilter,
          projectStatus: finalFilter?.projectStatus,
          ...replaceIdsParams(finalFilter?.projectIds),
          ...(channelOwnerId && {authorIds: [channelOwnerId]}),
          limit: finalFilter?.limit,
          offset: PROJECTS_COUNT_START_OFFSET,
          isContextIgnored: true,
          isLite: false,
          mainPage: false
      } : {
        ...finalFilter,
        ...onlyBooksParam,
        limit: finalFilter?.limit,
        offset: PROJECTS_COUNT_START_OFFSET,
        isLite: false
      }, { signal })
        .then((d) => d.json())
        .then(({ data, paging }) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data,
            paging,
            currentTab,
            currentCategory,
            entityType: mainTab.entityType,
            isDrafts: mainTab.isDrafts,
            isFinalPage: paging.pages === paging.page,
            contextName
          }))
        })
    }
    
    const restTabs = tabs[currentCategory].filter(({ path, isSwitcher }: any) => path !== currentTab && !isSwitcher);

    const json = Promise.allSettled(

      restTabs.map((tab: Tabs) => {
        //todo Убрать дублирование. Убрать ситуационные пропсы для разных страниц, оставить только конфиг и прокидывать нужное сверху.
        const isCompetitions = isCompetitionsProp || tab?.entityType === 'competitions_new';
        const isNoTabs = currentTab === "all" && Object.values(tabs).length === 1;
        //@ts-ignore
        const hasOrFilter = !!tab?.filter?.orFilter && tab.filter.orFilter.length > 0 && !isNoTabs;
        const hasMainFilterOrFilter = !!filter?.orFilter && filter.orFilter.length > 0;

        const finalFilter = getFinalFilter(tab, filter, paramsForReplace, isNoTabs);
        
        const onlyBooksParam = isSchool || isLMSHSE || isPeoplePage || isProfile || isCompetitions ? {} : !finalFilter.hasOwnProperty('onlyBooks') ? { onlyBooks: false} : {};

        //@ts-ignore
        const method: APIMethod<any, any> = ['people_in_detail', 'people']?.includes(tab.entityType)? methods['designer'] : isCompetitions ? methods['competition'] : (hasOrFilter || hasMainFilterOrFilter) ? methods.mainList : (methods[tab?.entityType] || methods.main);

        if (tab?.entityType === DesignerTab.subscriptionsProjects ) {
          return (
            methods.feed({
              ...finalFilter,
              offset: PROJECTS_COUNT_START_OFFSET,
              accountId: accountId,
              limit: finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET,
              ...(!!filter?.sort ? { sort: filter.sort} : {}),
              ...(!!filter?.tagIds ? { tagIds: filter.tagIds} : {}),
              ...(!!filter.hasProductCards ? { hasProductCards: filter.hasProductCards} : {})
            })
              .then((d) => d.json())
              .then((d) => {
                return ({
                  data: d,
                  path: tab.path,
                  entityType: tab.entityType,
                  isDrafts: tab.isDrafts,
                })
              })
          )
        }
        if(tab?.entityType === DesignerTab.subscriptions) {
          const visibilityOpt = `${!isMe ? `&visibility=${SUBSCRIBE_VISIBILITY_STATUS.visible}` : ''}`;
          const offset = finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
          const subscriberType = '0'
          const targetType = 'userProjects';
          const params = `subscriberId=${accountId}&subscriberType=${subscriberType}&targetType=${targetType}&limit=${offset}${visibilityOpt}&page=1`
          const url = `${Endpoints.subscriptions}/api/Subscription/GetSubscriptions?${params}`

          return API.fetch(url, { signal })
          .then((r) => r.json())
          .then((subscriptions) => {
            const { data, paging } = subscriptions;
            const subsIds = data?.map((i: any) => i?.targetId);

            if (!subsIds?.length) return;

            return DesignerAPI.GetList({
              designerIds: subsIds,
              offset: offset
            }).then((r) => r.json())
            .then((r) => {
              return ({
                data: {
                  paging, 
                  // @ts-ignore
                  data: mergeArraysBySubscribedId(r?.data, data) 
                },
                path: tab.path,
                entityType: tab.entityType,
                isFinalPage: paging?.pages === paging.page,
              })
            })
          })
        }
        if(tab?.entityType === DesignerTab.subscribers) {

          const offset = finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
          const targetType = 'userProjects'
          const params = `targetId=${accountId}&targetType=${targetType}&limit=${offset}&page=1`
          const url = `${Endpoints.subscriptions}/api/Subscription/GetSubscribers?${params}`
    
          return API.fetch(url, { signal })
          .then((r) => r.json())
          .then(async(subscriptions) => {
            const { data, paging } = subscriptions;
            const subsIds = data?.map((i: any) => i?.subscriberId);

            if (!subsIds?.length) return;

            return DesignerAPI.GetList({
              designerIds: subsIds,
              offset: finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
            }).then((r) => r.json())
            .then((r) => {
              return ({
                // @ts-ignore
                data: { paging, data: mergeArraysBySubscribedId(r?.data, data) },
                path: tab.path,
                entityType: tab.entityType,
                isFinalPage: paging?.pages === paging.page,
              })
            })
          })
        }
        if (tab?.entityType === 'discount') {
          return API.request()
          .endpoint(Endpoints.geograffee)
          .url('/api/Designer/GetDesignerDiscountCards')
          .addQuery('designerId', designerId)
          .get()
          .send({ signal })
          .then((r) => r.json())
          .then((cards) => {
          return ({
            data: {
              data: cards,
              paging: {count : 20, page: 1, pages: 1},
            },
            path: tab.path,
            entityType: tab.entityType,
            isDrafts: false,
            isFinalPage: true,
          })
          }
          )
        }
        if (tab?.entityType === DesignerTab.competitionCerts) {
          return API.request()
          .endpoint(Endpoints.geograffee)
            .url('/api/Designer/GetCompetitionCerts')
            .addQuery('designerId', designerId)
            .get()
            .send({ signal })
            .then((r) => r.json())
            .then((cards) => {
              return ({
                data: {
                  data: cards,
                  paging: {count : 20, page: 1, pages: 1},
                },
                path: tab.path,
                entityType: tab.entityType,
                isDrafts: false,
                isFinalPage: true,
              })
            });
        }
        if (tab?.entityType === 'competitions') {
          return ProjectAPI.GetParticipateCompetitions()
            .then((d) => d.json())
            .then((d) => {
              return ({
                data: {
                  data: d,
                  paging: {
                    pages: 1,
                    page: 1,
                    count: d.totalProjectsCount,
                  },
                },
                paging: {
                  pages: 1,
                  page: 1,
                  count: d.totalProjectsCount,
                },
                path: tab.path,
                entityType: tab.entityType,
                isDrafts: tab.isDrafts,
                isFinalPage: true,
              })
            });
        }
        if(tab?.entityType === 'modules'){
            return LMSAPI.GetModulesList({
            teamId: teamId,
            ...(tab?.path && tab.path !== 'all' && { lMSModuleDisciplines: tab.path }),
            limit: finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
        })
            .then((d) => d.json())
            .then((d) => {
              return ({
                data: d,
                path: tab.path,
                entityType: tab.entityType,
                isDrafts: tab.isDrafts,
              })
            })
        }
        if(mainTab?.entityType === 'organizations' && !isProfile && !isCompetitions && isLMSHSE && !isPeoplePage){
          
          return (
            method({
              ...finalFilter,
              ...(finalFilter?.sort ? { sort: finalFilter.sort } : {}),
              offset: PROJECTS_COUNT_START_OFFSET,
                //todo выяснить, нужно ли это сейчас
              // lMSCourseType: (mainTab?.entityType === 'organizations' && !isProfile && !isCompetitions) && lMSCourseType,
            })
              .then((d) => d.json())
              .then((d) => {
                return ({
                  data: d,
                  path: tab.path,
                  entityType: tab.entityType,
                  isDrafts: tab.isDrafts,
                })
              })
          )
        }
        if (
          isPeoplePage
          && [
            ContextViewTab.teachers,
            ContextViewTab.students,
            ContextViewTab.alumnus
          ]?.includes(tab.name as any)
        ) {
          const {
            limit,
            globalTypes,
            sort,
            isLite,
            isContextIgnored,
            appNameIgnore
          } = tab?.filter as any;
            return DesignerAPI.GetList({
              limit,
              globalTypes,
              sort,
              isLite,
              isContextIgnored,
              appNameIgnore
            }).then((r) => r.json())
            .then((r) => {
              return ({
                data: r?.data,
                path: tab.path,
                entityType: tab.entityType,
                isDrafts: tab.isDrafts,
              })
            })
        }

        return (
          method(isChannel ? {
              projectFilter: finalFilter?.projectFilter,
              projectStatus: finalFilter?.projectStatus,
              ...replaceIdsParams(finalFilter?.projectIds),
              ...(channelOwnerId && {authorIds: [channelOwnerId]}),
              limit: finalFilter?.limit,
              offset: PROJECTS_COUNT_START_OFFSET,
              isContextIgnored: true,
              isLite: false,
              mainPage: false
          } : {
            ...finalFilter,
            ...onlyBooksParam,
              ...(isChannel && {
                isContextIgnored: true, 
                mainPage: false, 
                ...replaceIdsParams(finalFilter?.projectIds),
                projectStatus: finalFilter?.projectStatus,
              }),
              ...(isProfile ? { isLite: false } : {}),
              offset: PROJECTS_COUNT_START_OFFSET,
          })
            .then((d) => d.json())
            .then((d) => {
              return ({
                data: d,
                path: tab.path,
                entityType: tab.entityType,
                isDrafts: tab.isDrafts,
              })
            })
        )
      }))
      .then((res: any) => {
          return {
            contextName,
            currentCategory,
            data: res.reduce((acc: any, tab: any) => {
              if (tab?.status !== 'fulfilled') return acc;
              const { value } = tab;
              if (!value) return acc;
              const { data: { data, paging }, path, entityType, isDrafts } = value;
              return {
                ...acc,
                [path]: {
                  data,
                  paging,
                  entityType,
                  isDrafts,
                  isFinalPage: paging.pages === paging.page
                },
              }
            }, {})
          };
        }
      )
      //временная затычка, удалить, когда у этой вкладки появятся свой запрос
    return json;
  }
);

export const fetchProjectsGridMore = createAsyncThunk(
  'projects/fetchProjectsGridMore',
  async (args: any, { getState }: { getState: any }) => {
    const { currentTab, currentCategory, isChannel, filter, accountId, teachersOnly, isLMSHSE, isProfile, isCompetitions, isMe, isMedia, isPeoplePage, isSchool, contextName, isHseSpaceDesignerPage } = args;
    const { tabs, data } = getState().projectsGrid;

    const tab = tabs[currentCategory].find(({ path }: any) => path === currentTab);
    const { paging }: any = data[currentCategory][currentTab];

    const paramsForReplace = {
      ...(teachersOnly ? {
        globalTypes: ['Cooperator', 'Employee'],
      } : {}),
      ...(isSchool && currentTab === ContextViewTab.students ? {
        globalTypes: ['Hseyerevanstudent'],
      } : {}),
      ...(isHseSpaceDesignerPage ? {
        globalTypes: [...tab.filter.globalTypes, 'hsecreativestudent'],
        contexts: ['hsespace', 'hsedesign']
      } : {}),
    }

    const isNoTabs = currentTab === "all" && Object.values(tabs).length === 1;
    const hasOrFilter = !!tab?.filter?.orFilter && tab.filter.orFilter.length > 0 && !isNoTabs;
    const hasMainFilterOrFilter = !!filter?.orFilter && filter.orFilter.length > 0;
    const finalFilter = getFinalFilter(tab, filter, paramsForReplace, isNoTabs);

    const onlyBooksParam = isSchool || isLMSHSE || isPeoplePage || isProfile || isCompetitions ? {} : !finalFilter.hasOwnProperty('onlyBooks') ? { onlyBooks: false} : {};

    const method: APIMethod<any, any> = 
      tab?.entityType === DesignerTab.subscriptionsProjects ? methods.feed : 
      isPeoplePage || ['people_in_detail', 'people']?.includes(tab.entityType) ?
      methods.designer :
      //@ts-ignore
      (hasOrFilter || hasMainFilterOrFilter) ? methods.mainList : (methods[tab?.entityType] || methods.main);

      if (tab?.entityType === DesignerTab.subscriptions) {
        const json = await urlApi()?.fetchSubscriptions({ 
            subscriberId: accountId, 
            page: paging.page + 1,
            offset: paging.offset + filter.limit,
            limit: PROJECTS_COUNT_FOR_OFFSET,
            tab,
            isMe,
          })
        
        return {
          currentTab,
          currentCategory,
          entityType: tab.entityType,
          paging: json.paging,
          data: json.data,
          //@ts-ignore
          isFinalPage: json.paging.pages === json?.paging?.page
        };
      }

      if (tab?.entityType === DesignerTab.subscribers) {
        const json = await urlApi()?.fetchSubscribers({ 
          subscriberId: accountId, 
          page: paging.page + 1,
          offset: paging.offset + filter.limit,
          limit: PROJECTS_COUNT_FOR_OFFSET,
          tab,
        })

        return {
          currentTab,
          currentCategory,
          entityType: tab.entityType,
          paging: json.paging,
          data: json.data,
          //@ts-ignore
          isFinalPage: json.paging.pages === json?.paging?.page
        };
      }

    const json = await API.Json(
      method({
        ...finalFilter,
        ...onlyBooksParam,
        isLite: false,
        offset: paging.offset + filter.limit,
        page: paging.page + 1,
        ...(isChannel ? {
          ...replaceIdsParams(finalFilter?.projectIds), 
          projectStatus: finalFilter?.projectStatus,
        } : {}),
        ...(tab?.entityType === DesignerTab.subscriptionsProjects ? { accountId } : {}),
        ...(tab?.withoutEmptyParams ? {} : filter?.sort ? { sort: filter?.sort} : {})
      }),
    )

    return {
      currentTab,
      currentCategory,
      entityType: tab.entityType,
      paging: json.paging,
      data: json.data,
      //@ts-ignore
      isFinalPage: json.paging.pages <= json?.paging?.page
    };
  }
);

export const fetchInfiniteProjectsWithUpdate = createAsyncThunk(
  'projects/fetchInfiniteProjectsWithUpdate',
  async (args: any, { getState }: { getState: any }) => {
    const { page, lang } = args;
    const { filter, paging } = getState().projects;

    const json = await API.Json(
      ProjectAPI.GetMainPage({
        ...filter,
        offset: calcOffsetValue(
          (Number(page) * PROJECTS_COUNT_FOR_OFFSET
          ),
          paging?.count
        ) - PROJECTS_COUNT_FOR_OFFSET
      }),
    )
    return lang ? (API.lang({
      paging: json.paging,
      data: [...json.data],
      //@ts-ignore
      isFinalPage: json.paging.pages <= json?.paging?.page
    }, lang) as any) : {
      paging: json.paging,
      data: [...json.data],
      //@ts-ignore
      isFinalPage: json.paging.pages <= json?.paging?.page
    };
  }
);
